/**
 * @fileoverview O layout das telas de entrada (entrar, criar conta, esqueci a
 * senha, nova senha, confirmar o e-mail): à esquerda, o formulário; à direita,
 * uma mini apresentação do produto para quem ainda não conhece: a promessa, uma
 * conversa de verdade (o cliente pergunta, a IA responde e passa a conversa
 * para a equipe) e três benefícios. A conversa entra numa sequência só, uma
 * vez por abertura da página (com o movimento reduzido, já aparece pronta).
 * Em tela baixa, a conversa fica sem a lista ao lado e os benefícios só com
 * o título; no celular, só o formulário.
 */

import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { CssBaseline, Fade, IconButton, Popover } from "@material-ui/core";
import { ArrowRightLeft, Bot, BotMessageSquare, Globe, MessagesSquare, Sprout } from "lucide-react";

import LanguageControl from "../LanguageControl";
import { i18n } from "../../translate/i18n";
// Os clientes da conversa de exemplo: fotos geradas por IA (não são pessoas reais).
import carlaPhoto from "../../assets/showcase/carla.jpg";
import joaoPhoto from "../../assets/showcase/joao.jpg";
import lucasPhoto from "../../assets/showcase/lucas.jpg";
// A versão do sistema, a do package.json (na build, só o número vai para o site).
import { version as appVersion } from "../../../package.json";

// A sequência da conversa (ms desde a abertura): cada mensagem entra depois da anterior.
const T = { customer1: 300, aiTyping: 900, ai: 1900, customer2: 2900, handoff: 3700, humanTyping: 4300, human: 5200 };

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const faint = "#71717A";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const surface = dark ? "#18181B" : "#FFFFFF";
  const tint = dark ? "rgba(255,255,255,0.05)" : "#F4F4F5";
  const ok = dark ? "#4ADE80" : "#15803D";
  const reduced = "@media (prefers-reduced-motion: reduce)";
  const short = "@media (max-height: 820px)";
  const veryShort = "@media (max-height: 700px)";
  const narrowShort = "@media (max-width: 1279px) and (max-height: 820px)";
  const enter = (delay) => ({
    opacity: 0,
    animation: `$bubbleIn 380ms ease-out ${delay}ms forwards`,
    [reduced]: { opacity: 1, animation: "none" },
  });
  // O "digitando": aparece, fica e some sem deixar o espaço.
  const typing = (from, to) => ({
    maxHeight: 0, opacity: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0, borderTopWidth: 0, borderBottomWidth: 0, overflow: "hidden",
    animation: `$typingLife ${to - from}ms ease-out ${from}ms both`,
    [reduced]: { display: "none" },
  });
  return {
    root: {
      minHeight: "100vh",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.05fr)",
      background: dark ? "#0A0A0A" : "#FFFFFF",
      color: text,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    formSide: { display: "flex", flexDirection: "column", padding: "28px 40px 24px", minWidth: 0, [theme.breakpoints.down("xs")]: { padding: "20px 16px" } },
    top: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    brand: { display: "flex", alignItems: "center", gap: 10, color: text },
    // A logo é branca: num quadrado escuro, aparece nos dois temas.
    mark: {
      width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
      background: dark ? "#1C1C1F" : "#09090B", border: dark ? `1px solid ${line}` : "none",
      "& img": { width: 22, height: 22, objectFit: "contain", display: "block" },
    },
    wordmark: { fontSize: 15, fontWeight: 700, letterSpacing: "-0.2px" },
    language: { color: muted, "&:focus-visible": { outline: `1px solid ${dark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)"}` } },
    languageOpen: { color: text, background: tint },
    // O seletor de idioma: a superfície do app, borda fina e uma entrada curta.
    languagePaper: {
      marginTop: 6, borderRadius: 12, background: surface, border: `1px solid ${line}`,
      boxShadow: dark ? "0 16px 40px rgba(0,0,0,0.5)" : "0 16px 40px rgba(15,23,42,0.12)",
      transformOrigin: "top right", animation: "$menuIn 160ms ease-out", [reduced]: { animation: "none" },
    },
    "@keyframes menuIn": { from: { opacity: 0, transform: "translateY(-4px) scale(0.98)" }, to: { opacity: 1, transform: "none" } },
    formWrap: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 0", [theme.breakpoints.down("sm")]: { alignItems: "flex-start", paddingTop: 40 } },
    form: { width: "100%", maxWidth: 400 },
    footer: { display: "flex", flexWrap: "wrap", gap: 14, fontSize: 12.5, color: faint },

    showcase: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      gap: 28,
      padding: "40px 64px",
      background: dark ? "#121214" : "#F4F4F5",
      borderLeft: `1px solid ${line}`,
      overflow: "hidden",
      [theme.breakpoints.down("md")]: { padding: "40px 40px" },
      [theme.breakpoints.down("sm")]: { display: "none" },
      [short]: { gap: 18, paddingTop: 24, paddingBottom: 24 },
    },
    intro: { maxWidth: 640 },
    headline: {
      margin: 0, fontSize: 30, lineHeight: 1.18, fontWeight: 700, letterSpacing: "-0.8px", textWrap: "balance", color: text,
      [theme.breakpoints.down("md")]: { fontSize: 28 },
      [short]: { fontSize: 26 },
      [narrowShort]: { fontSize: 24 },
    },
    lead: {
      margin: "12px 0 0", fontSize: 15.5, lineHeight: 1.6, color: muted, maxWidth: 560, textWrap: "pretty",
      [short]: { marginTop: 8, fontSize: 14.5 },
      [narrowShort]: { fontSize: 14, lineHeight: 1.5 },
      [veryShort]: { display: "none" },
    },

    preview: {
      width: "100%",
      maxWidth: 580,
      display: "grid",
      gridTemplateColumns: "180px minmax(0, 1fr)",
      background: surface,
      border: `1px solid ${line}`,
      borderRadius: 16,
      boxShadow: dark ? "0 24px 48px rgba(0,0,0,0.45)" : "0 24px 48px rgba(24,24,27,0.10)",
      overflow: "hidden",
      fontSize: 13,
      // Sem espaço (tela estreita ou baixa), só a conversa: os balões ficam mais curtos.
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr", "& $list": { display: "none" } },
      [short]: { gridTemplateColumns: "1fr", "& $list": { display: "none" } },
    },
    list: { borderRight: `1px solid ${line}`, padding: "10px 0", background: dark ? "#161618" : "#FCFCFC" },
    row: { display: "flex", alignItems: "center", gap: 10, padding: "9px 12px" },
    rowActive: { background: tint },
    // O fundo das fotos é quase preto: no escuro, um contorno leve mostra o círculo.
    avatar: {
      width: 30, height: 30, borderRadius: "50%", flexShrink: 0, display: "block", objectFit: "cover",
      background: dark ? "rgba(255,255,255,0.07)" : "#E4E4E7", boxShadow: dark ? "0 0 0 1px rgba(255,255,255,0.1)" : "none",
    },
    rowText: { minWidth: 0, flex: 1 },
    rowName: { display: "block", fontWeight: 600, color: text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    rowLast: { display: "block", fontSize: 12, color: faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 2 },

    chat: { display: "flex", flexDirection: "column", minWidth: 0 },
    chatHead: { display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderBottom: `1px solid ${line}` },
    chatWho: { flex: 1, minWidth: 0 },
    chatName: { display: "block", fontWeight: 600, color: text },
    chatSub: { display: "block", fontSize: 11.5, color: faint },
    // Quem atende agora: a IA e, depois da transferência, a Ana.
    status: { position: "relative", height: 24, minWidth: 112 },
    statusPill: {
      position: "absolute", right: 0, top: 0, height: 24, display: "inline-flex", alignItems: "center", gap: 5, padding: "0 9px",
      borderRadius: 999, fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    },
    statusAi: {
      color: text, background: tint, border: `1px solid ${line}`,
      animation: `$fadeOut 260ms ease-out ${T.handoff}ms forwards`,
      [reduced]: { animation: "none", opacity: 0 },
    },
    statusHuman: {
      color: ok, background: dark ? "rgba(74,222,128,0.10)" : "#F0FDF4", border: `1px solid ${dark ? "rgba(74,222,128,0.22)" : "#BBF7D0"}`,
      opacity: 0, animation: `$fadeIn 320ms ease-out ${T.handoff + 120}ms forwards`,
      [reduced]: { animation: "none", opacity: 1 },
    },
    dot: { width: 6, height: 6, borderRadius: "50%", background: "currentColor" },

    messages: { display: "flex", flexDirection: "column", padding: "4px 14px 14px", background: dark ? "#141416" : "#FAFAFA", [short]: { padding: "2px 12px 12px" } },
    bubble: { maxWidth: "84%", marginTop: 10, padding: "8px 11px", borderRadius: 12, lineHeight: 1.45, [short]: { marginTop: 8, padding: "7px 10px" } },
    fromCustomer: { alignSelf: "flex-start", background: surface, border: `1px solid ${line}`, color: text, borderTopLeftRadius: 4 },
    customer1: enter(T.customer1),
    customer2: enter(T.customer2),
    // A IA em destaque: o balão escuro com o selo "IA", como no chat do app.
    fromAi: { alignSelf: "flex-end", background: dark ? "#2E3038" : "#18181B", color: "#FAFAFA", borderTopRightRadius: 4, ...enter(T.ai) },
    sender: { display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 700, marginBottom: 3, opacity: 0.85 },
    aiBadge: { padding: "0 5px", borderRadius: 4, fontSize: 9.5, lineHeight: "15px", letterSpacing: 0.3, background: "rgba(255,255,255,0.16)" },
    fromHuman: { alignSelf: "flex-end", background: surface, border: `1px solid ${line}`, color: text, borderTopRightRadius: 4, ...enter(T.human) },
    humanSender: { color: muted },
    typingBubble: {
      alignSelf: "flex-end", display: "inline-flex", alignItems: "center", gap: 4, paddingLeft: 12, paddingRight: 12, borderRadius: 12, borderTopRightRadius: 4,
      "& span": { width: 5, height: 5, borderRadius: "50%", background: "currentColor", opacity: 0.4, animation: "$blink 1.1s infinite ease-in-out" },
      "& span:nth-child(2)": { animationDelay: "0.15s" },
      "& span:nth-child(3)": { animationDelay: "0.3s" },
    },
    aiTyping: { background: dark ? "#2E3038" : "#18181B", color: "#FAFAFA", ...typing(T.aiTyping, T.ai) },
    humanTyping: { background: surface, border: `1px solid ${line}`, color: muted, ...typing(T.humanTyping, T.human) },
    // A transferência: o momento em que a IA chama a equipe.
    handoff: {
      display: "flex", alignItems: "center", gap: 10, marginTop: 12, color: muted, fontSize: 11.5, fontWeight: 600,
      "&::before, &::after": { content: '""', flex: 1, height: 1, background: line },
      ...enter(T.handoff),
    },
    // O verde discreto do "Com Ana": a conversa mudou de quem atende.
    handoffPill: {
      display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 999, fontSize: 12, color: ok,
      background: dark ? "rgba(74,222,128,0.10)" : "#F0FDF4", border: `1px solid ${dark ? "rgba(74,222,128,0.24)" : "#BBF7D0"}`,
      animation: `$ring 900ms ease-out ${T.handoff + 200}ms`,
      [reduced]: { animation: "none" },
    },
    // Já entrou nesta abertura da página: aparece pronta.
    still: {
      "& $customer1, & $customer2, & $fromAi, & $fromHuman, & $handoff": { opacity: 1, animation: "none" },
      "& $aiTyping, & $humanTyping": { display: "none" },
      "& $statusAi": { opacity: 0, animation: "none" },
      "& $statusHuman": { opacity: 1, animation: "none" },
      "& $handoffPill": { animation: "none" },
    },

    benefits: { listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 14, maxWidth: 600, [short]: { gap: 10 } },
    benefit: { display: "grid", gridTemplateColumns: "34px minmax(0, 1fr)", gap: 12, alignItems: "start" },
    benefitIcon: {
      width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
      background: surface, border: `1px solid ${line}`, color: text,
    },
    benefitTitle: { display: "block", fontSize: 14.5, fontWeight: 650, lineHeight: 1.35, color: text, paddingTop: 1 },
    benefitText: { display: "block", fontSize: 13.5, lineHeight: 1.5, color: muted, marginTop: 2, [short]: { display: "none" } },

    "@keyframes bubbleIn": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "none" } },
    "@keyframes typingLife": {
      "0%": { opacity: 0, maxHeight: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0, borderTopWidth: 0, borderBottomWidth: 0 },
      "18%": { opacity: 1, maxHeight: 40, marginTop: 10, paddingTop: 9, paddingBottom: 9, borderTopWidth: 1, borderBottomWidth: 1 },
      "82%": { opacity: 1, maxHeight: 40, marginTop: 10, paddingTop: 9, paddingBottom: 9, borderTopWidth: 1, borderBottomWidth: 1 },
      "100%": { opacity: 0, maxHeight: 0, marginTop: 0, paddingTop: 0, paddingBottom: 0, borderTopWidth: 0, borderBottomWidth: 0 },
    },
    "@keyframes blink": { "0%, 80%, 100%": { opacity: 0.25 }, "40%": { opacity: 0.9 } },
    "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
    "@keyframes fadeOut": { from: { opacity: 1 }, to: { opacity: 0 } },
    "@keyframes ring": {
      "0%": { boxShadow: `0 0 0 0 ${dark ? "rgba(74,222,128,0.35)" : "rgba(21,128,61,0.28)"}` },
      "100%": { boxShadow: "0 0 0 10px rgba(0,0,0,0)" },
    },
  };
});

// A prévia entra uma vez por abertura da página: ir de uma tela de entrada a
// outra (login, esqueci a senha, nova senha) não a repete.
let previewPlayed = false;

const BENEFITS = [
  { key: "together", Icon: MessagesSquare },
  { key: "ai", Icon: BotMessageSquare },
  { key: "free", Icon: Sprout },
];

const Showcase = ({ classes, animate }) => (
  <aside className={classes.showcase} aria-label={i18n.t("auth.showcase.label")}>
    <div className={classes.intro}>
      <h2 className={classes.headline}>{i18n.t("auth.showcase.headline")}</h2>
      <p className={classes.lead}>{i18n.t("auth.showcase.lead")}</p>
    </div>

    <div className={`${classes.preview}${animate ? "" : ` ${classes.still}`}`} aria-hidden="true">
      <div className={classes.list}>
        {[
          { photo: carlaPhoto, name: "Carla Mendes", last: i18n.t("auth.showcase.preview.customer"), active: true },
          { photo: joaoPhoto, name: "João Ribeiro", last: i18n.t("auth.showcase.preview.otherLast1") },
          { photo: lucasPhoto, name: "Lucas Santos", last: i18n.t("auth.showcase.preview.otherLast2") },
        ].map((row) => (
          <div key={row.name} className={`${classes.row}${row.active ? ` ${classes.rowActive}` : ""}`}>
            <img className={classes.avatar} src={row.photo} alt="" />
            <span className={classes.rowText}>
              <span className={classes.rowName}>{row.name}</span>
              <span className={classes.rowLast}>{row.last}</span>
            </span>
          </div>
        ))}
      </div>
      <div className={classes.chat}>
        <div className={classes.chatHead}>
          <img className={classes.avatar} src={carlaPhoto} alt="" />
          <span className={classes.chatWho}>
            <span className={classes.chatName}>Carla Mendes</span>
            <span className={classes.chatSub}>WhatsApp</span>
          </span>
          <span className={classes.status}>
            <span className={`${classes.statusPill} ${classes.statusAi}`}>
              <Bot size={13} /> {i18n.t("auth.showcase.preview.statusAi")}
            </span>
            <span className={`${classes.statusPill} ${classes.statusHuman}`}>
              <span className={classes.dot} /> {i18n.t("auth.showcase.preview.statusHuman")}
            </span>
          </span>
        </div>
        <div className={classes.messages}>
          <div className={`${classes.bubble} ${classes.fromCustomer} ${classes.customer1}`}>{i18n.t("auth.showcase.preview.customer")}</div>
          <div className={`${classes.typingBubble} ${classes.aiTyping}`}><span /><span /><span /></div>
          <div className={`${classes.bubble} ${classes.fromAi}`}>
            <span className={classes.sender}>
              <Bot size={12} /> {i18n.t("auth.showcase.preview.aiName")}
              <span className={classes.aiBadge}>{i18n.t("auth.showcase.preview.aiTag")}</span>
            </span>
            {i18n.t("auth.showcase.preview.ai")}
          </div>
          <div className={`${classes.bubble} ${classes.fromCustomer} ${classes.customer2}`}>{i18n.t("auth.showcase.preview.customer2")}</div>
          <div className={classes.handoff}>
            <span className={classes.handoffPill}>
              <ArrowRightLeft size={13} /> {i18n.t("auth.showcase.preview.handoff")}
            </span>
          </div>
          <div className={`${classes.typingBubble} ${classes.humanTyping}`}><span /><span /><span /></div>
          <div className={`${classes.bubble} ${classes.fromHuman}`}>
            <span className={`${classes.sender} ${classes.humanSender}`}>{i18n.t("auth.showcase.preview.humanName")}</span>
            {i18n.t("auth.showcase.preview.human")}
          </div>
        </div>
      </div>
    </div>

    <ul className={classes.benefits}>
      {BENEFITS.map(({ key, Icon }) => (
        <li key={key} className={classes.benefit}>
          <span className={classes.benefitIcon}><Icon size={17} /></span>
          <span>
            <span className={classes.benefitTitle}>{i18n.t(`auth.showcase.benefits.${key}.title`)}</span>
            <span className={classes.benefitText}>{i18n.t(`auth.showcase.benefits.${key}.text`)}</span>
          </span>
        </li>
      ))}
    </ul>
  </aside>
);

const AuthLayout = ({ children }) => {
  const classes = useStyles();
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [animate] = useState(() => !previewPlayed);
  useEffect(() => {
    previewPlayed = true;
  }, []);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <div className={classes.formSide}>
        <div className={classes.top}>
          <span className={classes.brand}>
            <span className={classes.mark}><img src="/logo-original.png" alt="" /></span>
            <span className={classes.wordmark}>Sttelar Talk</span>
          </span>
          <IconButton
            className={`${classes.language}${languageAnchor ? ` ${classes.languageOpen}` : ""}`}
            aria-label={i18n.t("auth.language")}
            aria-haspopup="true"
            aria-expanded={Boolean(languageAnchor)}
            onClick={(event) => setLanguageAnchor(event.currentTarget)}
          >
            <Globe size={20} />
          </IconButton>
          <Popover
            anchorEl={languageAnchor}
            open={Boolean(languageAnchor)}
            onClose={() => setLanguageAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            TransitionComponent={Fade}
            transitionDuration={{ enter: 160, exit: 120 }}
            PaperProps={{ className: classes.languagePaper }}
          >
            <LanguageControl showHeader onChange={() => setLanguageAnchor(null)} />
          </Popover>
        </div>
        <main className={classes.formWrap}>
          <div className={classes.form}>{children}</div>
        </main>
        <div className={classes.footer}>
          <span>© {new Date().getFullYear()} Sttelar</span>
          <span>{i18n.t("auth.version", { version: appVersion })}</span>
        </div>
      </div>
      <Showcase classes={classes} animate={animate} />
    </div>
  );
};

export default AuthLayout;
